import { createContext, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotify } from '@/components/snackbar';
import { getProductService } from '@/services/products';
import { getProductLinksService } from '@/services/product-links';
import { createOfferService, updateOfferService, deleteOfferService } from '@/services/offers';
import { CACHE_TIME, STALE_TIME, STALE_TIME_SHORT } from '@/services/constants';

export const OfferContext = createContext({
    isBound: false,
    productId: null,
    product: null,
    offers: [],
    mainOffer: null,
    secondaryOffers: [],
    linksByOfferId: {},
    isFetchingOffers: false,
    isOffersError: false,
    offersError: null,
    refetchOffers: async () => { },
    createOffer: async () => { },
    isCreatingOffer: false,
    updateOffer: async () => { },
    isUpdatingOffer: false,
    deleteOffer: async () => { },
    isDeletingOffer: false,
});

const normalizeOffer = (offer) => ({
    ...offer,
    offerId: offer.offerId || offer.id,
    trial_days: offer?.trial_days ?? 0,
});

const OfferProvider = ({ children, productId: productIdProp, initialOffers }) => {
    const params = useParams();
    const routeId = params?.id;
    const id = productIdProp ?? routeId;
    const notify = useNotify();
    const queryClient = useQueryClient();
    const lastKnownOffersRef = useRef(
        Array.isArray(initialOffers) ? initialOffers.map(normalizeOffer) : []
    );

    useEffect(() => {
        if (Array.isArray(initialOffers) && initialOffers.length > 0) {
            lastKnownOffersRef.current = initialOffers.map(normalizeOffer);
        }
    }, [initialOffers]);

    const {
        data: productData,
        isLoading: isFetchingOffers,
        refetch: refetchOffers,
        isError: isOffersError,
        error: offersError,
    } = useQuery({
        queryKey: ['product', { id }],
        queryFn: () => getProductService({ id }),
        enabled: !!id,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
        refetchOnMount: false,
        select(data) {
            const mappedOffers = (data?.offers || []).map(normalizeOffer);
            return { ...data, offers: mappedOffers };
        },
    });

    const { data: linksData } = useQuery({
        queryKey: ['product-links', { productId: id }],
        queryFn: () => getProductLinksService({ product: id, limit: 100 }),
        enabled: !!id,
        staleTime: STALE_TIME_SHORT,
    });

    const linksByOfferId = useMemo(() => {
        const map = {};
        const results = linksData?.results || [];
        results.forEach((link) => {
            const offerId = link?.offer?.id;
            if (offerId && link?.url) {
                map[offerId] = link.url;
            }
        });
        return map;
    }, [linksData]);

    const offers = useMemo(() => {
        if (Array.isArray(productData?.offers)) {
            return productData.offers;
        }

        return lastKnownOffersRef.current;
    }, [productData?.offers]);
    const mainOffer = useMemo(() => offers.find((o) => o.default) || null, [offers]);
    const secondaryOffers = useMemo(() => offers.filter((o) => !o.default), [offers]);

    const product = useMemo(() => {
        if (!productData) return null;
        const currencyRaw =
            productData.currency ?? mainOffer?.currency ?? mainOffer?.currency_code ?? 'BRL';
        const currency =
            currencyRaw != null && String(currencyRaw).trim() !== ''
                ? String(currencyRaw).trim().toUpperCase()
                : 'BRL';
        return {
            type: productData.type,
            price: productData.price ?? mainOffer?.price,
            image: productData.image,
            short_id: productData.short_id,
            currency,
            paymentMethods: productData.paymentMethods || productData.paymentsOrder || [],
        };
    }, [productData, mainOffer]);

    const { mutateAsync: createOffer, isLoading: isCreatingOffer } = useMutation({
        mutationFn: async (payload) => {
            if (!id) throw new Error('[OfferContext] createOffer: productId indefinido.');
            const body = { ...payload, product: id };
            return createOfferService(body);
        },
        onSuccess: (createdOffer) => {
            notify.success('Oferta criada com sucesso');
            queryClient.setQueryData(['product', { id }], (prev) => {
                if (!prev) return prev;
                const prevOffers = prev.offers || [];
                const normalized = {
                    ...createdOffer,
                    offerId: createdOffer.id,
                    trial_days: createdOffer?.trial_days ?? 0,
                };
                lastKnownOffersRef.current = [...prevOffers, normalized];
                return { ...prev, offers: lastKnownOffersRef.current };
            });
            // Refetch links para a nova oferta aparecer com link na tabela
            queryClient.invalidateQueries({ queryKey: ['product-links', { productId: id }] });
        },
        onError: (error) => {
            notify.error(error);
        },
    });

    const { mutateAsync: updateOffer, isLoading: isUpdatingOffer } = useMutation({
        mutationFn: async ({ offerId, ...data }) => updateOfferService({ id: offerId, ...data }),
        onSuccess: (updatedOffer) => {
            notify.success('Oferta atualizada com sucesso');
            queryClient.setQueryData(['product', { id }], (prev) => {
                if (!prev) return prev;
                const prevOffers = prev.offers || [];
                const nextOffers = prevOffers.map((offer) =>
                    offer.id === updatedOffer.id || offer.offerId === updatedOffer.id
                        ? { ...offer, ...updatedOffer }
                        : offer
                );
                lastKnownOffersRef.current = nextOffers;
                return { ...prev, offers: nextOffers };
            });
        },
        onError: (error) => {
            notify.error(error);
        },
    });

    const { mutateAsync: deleteOffer, isLoading: isDeletingOffer } = useMutation({
        mutationFn: async ({ offerId }) => deleteOfferService({ id: offerId }),
        onSuccess: (_, { offerId }) => {
            notify.success('Oferta removida com sucesso');
            queryClient.setQueryData(['product', { id }], (prev) => {
                if (!prev) return prev;
                const prevOffers = prev.offers || [];
                const nextOffers = prevOffers.filter(
                    (offer) => offer.id !== offerId && offer.offerId !== offerId
                );
                lastKnownOffersRef.current = nextOffers;
                return { ...prev, offers: nextOffers };
            });
        },
        onError: (error) => {
            notify.error(error);
        },
    });

    const value = useMemo(
        () => ({
            isBound: true,
            productId: id,
            product,
            offers,
            mainOffer,
            secondaryOffers,
            linksByOfferId,
            isFetchingOffers,
            isOffersError,
            offersError,
            refetchOffers,
            createOffer,
            isCreatingOffer,
            updateOffer,
            isUpdatingOffer,
            deleteOffer,
            isDeletingOffer,
        }),
        [
            id,
            product,
            offers,
            mainOffer,
            secondaryOffers,
            linksByOfferId,
            isFetchingOffers,
            isOffersError,
            offersError,
            refetchOffers,
            createOffer,
            isCreatingOffer,
            updateOffer,
            isUpdatingOffer,
            deleteOffer,
            isDeletingOffer,
        ]
    );

    return <OfferContext.Provider value={value}>{children}</OfferContext.Provider>;
};

OfferProvider.propTypes = {
    children: PropTypes.node.isRequired,
    productId: PropTypes.string,
    initialOffers: PropTypes.array,
};

export default OfferProvider;
